import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CurrentBudget } from './pages/CurrentBudget';
import { BudgetList } from './pages/BudgetList';
import { BudgetView } from './pages/BudgetView';
import { Navigation } from './components/Navigation';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './contexts/ToastContext';
import { NotFound } from './pages/NotFound';
import { RootRedirect } from './pages/RootRedirect';
import { Sentry } from './utils/sentry';
import { Analytics } from '@vercel/analytics/react';

// Import i18n instance and I18nextProvider
import './i18n';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { LandingPage } from './pages/LandingPage';
// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable caching to ensure we always fetch fresh data
      staleTime: 0,
      // Automatically refetch when window regains focus
      refetchOnWindowFocus: true,
      // Retry failed queries to handle network issues
      retry: 1,
    },
  },
});

// Create a Sentry-wrapped router for performance monitoring
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const AppLayout: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-100 transition-colors duration-200 dark:bg-gray-900">
      <Navigation />
      <main>
        <Outlet />
      </main>
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <ToastProvider>
            <ErrorBoundary>
              <I18nextProvider i18n={i18n}>
                <SentryRoutes>
                  {/* Root path redirects based on whether user has budgets */}
                  <Route path="/" element={<RootRedirect />} />

                  {/* Landing page - no navigation */}
                  <Route path="/landing" element={<LandingPage />} />

                  {/* App routes with navigation */}
                  <Route element={<AppLayout />}>
                    <Route path="/budgets/current" element={<CurrentBudget />} />
                    <Route path="/budgets" element={<BudgetList />} />
                    <Route path="/budgets/:id" element={<BudgetView />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </SentryRoutes>
              </I18nextProvider>
            </ErrorBoundary>
          </ToastProvider>
        </Router>
      </ThemeProvider>
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
      <Analytics />
    </QueryClientProvider>
  );
};
